import type { MenuDataItem } from '@ant-design/pro-components';

const root = '/product-management';

export const productMenuGroups = [
  {
    key: 'issues',
    names: [
      'category-review',
      'unsalable-product-report',
      'product-expiry-date-errors',
      'no-barcode-products',
    ],
  },
  { key: 'pricing', names: ['price-change-management', 'expired-product-management', 'discounts'] },
  { key: 'master-data', names: ['categories', 'brands', 'shipment-specs', 'product-change-logs'] },
  {
    key: 'strategy',
    names: [
      'supply-products',
      'featured-products',
      'banned-products',
      'product-stock-type',
      'carryover-product-management',
    ],
  },
  {
    key: 'presentation',
    names: ['product-descriptions', 'product-displays', 'hanshow-electronic-shelf-labels'],
  },
] as const;

/** Reorganizes only the rendered menu. The route table and access checks stay intact. */
export function groupProductManagementMenu(
  menu: MenuDataItem[],
  groupLabel: (key: (typeof productMenuGroups)[number]['key']) => string,
): MenuDataItem[] {
  return menu.map((item) => {
    if (item.path === '/data-anomaly') {
      return {
        ...item,
        children: item.children?.filter(
          (child) => child.path !== '/data-anomaly/product-expiry-date-errors',
        ),
      };
    }
    if (item.path !== root || !item.children) return item;
    const visible = item.children.filter((child) => !child.hideInMenu);
    const byPath = new Map(visible.map((child) => [child.path, child]));
    const products = byPath.get(`${root}/products`);
    const assigned = new Set<string>(products?.path ? [products.path] : []);
    const grouped: MenuDataItem[] = [];
    for (const { key, names } of productMenuGroups) {
      const children: MenuDataItem[] = names
        .map((name) => byPath.get(`${root}/${name}`))
        .filter((child): child is MenuDataItem => Boolean(child))
        .map((child) => ({ ...child, locale: false }));
      children.forEach((child) => {
        if (child.path) assigned.add(child.path);
      });
      if (children.length)
        grouped.push({
          key: `${root}/group-${key}`,
          path: `${root}/group-${key}`,
          name: groupLabel(key),
          locale: false,
          children,
        });
    }
    // Keep newly added pages visible until their product grouping is explicitly reviewed.
    const remaining = visible.filter((child) => !child.path || !assigned.has(child.path));
    return {
      ...item,
      children: [
        ...(products ? [{ ...products, locale: false as const }] : []),
        ...grouped,
        ...remaining,
      ],
    };
  });
}
